// src/pages/Help.js
// vHELP-004
//
// Helptekst StaffIQ.
// Opzet: kort waar het vanzelf spreekt, uitgebreider waar het proces extra uitleg vraagt.
// - Aanvragen → Matching → Plaatsingen aangepast aan nieuwe procesknoppen
// - Beschikbaarheid en Planning toegevoegd
// - CV-importtekst aangepast aan huidige werkwijze

import React from "react";

export default function Help() {
  const pageStyle = {
    maxWidth: 980,
    lineHeight: 1.55,
  };

  const sectionStyle = {
    marginTop: 22,
  };

  const smallStyle = {
    color: "#555",
  };

  return (
    <div style={pageStyle}>
      <h1>Help</h1>

      <p>
        StaffIQ is een praktisch CRM/HRM-systeem voor het MKB, personele
        dienstverlening en projectmatig werkende bedrijven. Het systeem helpt bij
        relaties, taken, documenten, offertes, opdrachten, facturen, personeel,
        matching, aanvragen, plaatsingen, beschikbaarheid en planning.
      </p>

      <p>
        De functies hebben zoveel mogelijk dezelfde opzet. Daardoor werkt een
        nieuwe functie meestal op dezelfde manier als een bekende functie.
      </p>

      <h2 style={sectionStyle}>Basisgebruik</h2>

      <p>
        Op laptop en pc staat links het menu, in het midden het overzicht en
        rechts het invoerblok. Klik op een regel in het overzicht om de gegevens
        rechts te openen. Op smartphone staan overzicht en invoerblok onder
        elkaar. Het menu open je met de drie streepjes linksboven.
      </p>

      <p>
        In de overzichten kun je zoeken, sorteren en filteren. Telefoonnummers,
        mobiele nummers en e-mailadressen zijn aanklikbaar. Bij een mobiel
        nummer kun je kiezen voor bellen, SMS of WhatsApp.
      </p>

      <p>
        De knop <b>Spreek</b> kan gebruikt worden als de browser
        spraakondersteuning heeft. Voorbeelden van spraakcommando&apos;s zijn:
        volgende, vorige, nieuw, vernieuw, item, selecteer, rechts, links, leeg,
        punt, open, klaar, ja, nee, aan en uit.
      </p>

      <h2 style={sectionStyle}>CRM voor het MKB</h2>

      <h3>Relaties en contactpersonen</h3>

      <p>
        In Relaties leg je organisaties en particulieren vast. Contactpersonen
        horen bij een relatie. De gegevens uit Relaties worden zoveel mogelijk
        automatisch gebruikt bij offertes, opdrachten, facturen, documenten,
        plaatsingen en communicatie.
      </p>

      <p>
        Bij een relatie kun je instellen of correspondentie en digitaal akkoord
        via WhatsApp en/of e-mail mag verlopen. Deze instellingen bepalen of
        akkoordverzoeken beschikbaar zijn bij offertes, opdrachten en
        plaatsingsbevestigingen voor de klant. Voor klantcommunicatie gebruikt
        StaffIQ de gegevens van de relatie zelf.
      </p>

      <h3>Meerdere bedrijfscodes</h3>

      <p>
        StaffIQ kan met meerdere bedrijfscodes werken. Hiermee kun je gegevens
        scheiden per administratie, vestiging, klantgroep of werkmaatschappij.
        Gebruikers zien alleen de bedrijfscodes waarvoor zij rechten hebben.
        De administrator beheert de bedrijfscodes en de rechten per gebruiker.
      </p>

      <p>
        Via het bedrijfscodeblok in het menu kun je kiezen met welke
        bedrijfscodes je wilt werken. Die selectie geldt voor CRM, HRM en de
        planning. De laatst gekozen selectie wordt per gebruiker onthouden.
      </p>

      <h3>Notities en taken</h3>

      <p>
        Notities en taken staan in één functie. Een notitie is vooral informatie.
        Een taak heeft een status, uitvoerder, vervaldatum en eventueel een
        herinnering.
      </p>

      <p>
        Bij het inloggen toont StaffIQ openstaande taken. Als een herinnering is
        ingesteld, kan de uitvoerder een e-mailherinnering krijgen zolang de taak
        nog niet op <b>Klaar</b> staat.
      </p>

      <h3>Documentbeheer</h3>

      <p>
        Documenten worden opgeslagen in een vaste mapstructuur per relatie of
        personeelslid. Ook door StaffIQ gemaakte documenten, zoals offertes,
        opdrachten, facturen, personeelskaarten, matchingdocumenten en
        plaatsingsbevestigingen, worden automatisch in de juiste map geplaatst.
      </p>

      <h3>Projectbeheer</h3>

      <p>
        In Projectbeheer kun je projecten en deelprojecten in niveaus vastleggen,
        bijvoorbeeld 01, 01.01 en 01.02. Per onderdeel kun je tekst en eventueel
        een prijs vastleggen. Deze gegevens kunnen worden gebruikt in offertes en
        opdrachten.
      </p>

      <h3>Offertes en opdrachten</h3>

      <p>
        In Offertes / Opdrachten maak je offertes met artikelregels, tekstregels,
        kopregels, subtotalen en totalen. Een goedgekeurde offerte kan worden
        omgezet naar een opdracht. Vanuit een opdracht kan later een conceptfactuur
        worden aangemaakt.
      </p>

      <p>
        Offerteregels kunnen worden toegevoegd vanuit Projectbeheer of handmatig.
        Regels kunnen worden verplaatst, zodat de volgorde van tekst, artikelen,
        kopregels, subtotalen en totalen logisch blijft.
      </p>

      <p>
        Via <b>Ter akkoord</b> kan een digitaal akkoordverzoek worden gemaakt.
        StaffIQ kiest op basis van de relatie-instellingen voor WhatsApp, e-mail
        of beide. De ontvanger krijgt een akkoordlink, kan de PDF bekijken en kan
        digitaal akkoord of niet akkoord geven.
      </p>

      <h3>Facturen</h3>

      <p>
        In Facturen controleer je conceptfacturen en maak je deze definitief.
        Daarna krijgt de factuur een factuurnummer en kan deze worden geprint of
        per e-mail worden verstuurd. Factuurteksten en factuursjablonen worden
        beheerd bij Instellingen.
      </p>

      <h2 style={sectionStyle}>HRM voor personele dienstverlening</h2>

      <h3>Personeel</h3>

      <p>
        In Personeel leg je kandidaten en medewerkers vast. De personeelskaart
        kan worden geprint, als document worden opgeslagen en worden gebruikt
        bij matching, plaatsingen, beschikbaarheid en planning.
      </p>

      <p>
        Bij een personeelslid kun je instellen of correspondentie en digitaal
        akkoord via WhatsApp en/of e-mail mag verlopen. Deze instellingen worden
        gebruikt bij akkoordverzoeken voor de kandidaat, bijvoorbeeld bij een
        plaatsingsbevestiging kandidaat.
      </p>

      <h3>CV importeren met AI</h3>

      <p>
        Bij het aanmaken van een nieuw personeelslid kun je een CV gebruiken om
        alvast gegevens te laten invullen. Kies bij <b>Personeel</b> eerst
        <b> + Nieuw</b> en daarna <b>CV importeren</b>.
      </p>

      <p>
        Met <b>CV importeren</b> kun je een PDF, Word-bestand of tekstbestand
        kiezen. StaffIQ gebruikt AI om de inhoud te lezen en vult daarna zoveel
        mogelijk velden alvast in.
      </p>

      <p>
        De AI kan onder andere naam, contactgegevens, adres, functie, opleiding,
        werkervaring, certificaten, talen, rijbewijs en een korte samenvatting
        herkennen. De gegevens worden <b>niet automatisch opgeslagen</b>.
        Controleer en verbeter de velden eerst zelf en klik daarna pas op
        <b>Opslaan</b>.
      </p>

      <h3>Matching</h3>

      <p>
        StaffIQ heeft matching voor personeel en relaties. Matching is een
        selectiehulpmiddel. Het systeem toont passende resultaten op basis van
        criteria zoals functie, regio, woonplaats, vervoer, rijbewijs, tarief,
        uurloon en profieltekst.
      </p>

      <p>
        Vanuit een aanvraag kun je direct naar <b>Match Personeel</b>. StaffIQ
        neemt de belangrijkste criteria mee en toont geschikte kandidaten. Na het
        kiezen van een kandidaat kan direct een conceptplaatsing worden gemaakt.
      </p>

      <h3>Aanvragen</h3>

      <p>
        In Aanvragen leg je de klantvraag vast: relatie, contactpersoon, functie,
        aantal personen, startdatum, uren per week, locatie, CAO, loonperiode,
        factuurperiode, factor, salarisindicatie, verkooptarief, reiskosten,
        eisen en opmerkingen.
      </p>

      <p>
        Als <b>Factor</b> en <b>Salarisindicatie</b> zijn ingevuld, berekent
        StaffIQ het <b>Verkooptarief</b> automatisch als factor x
        salarisindicatie. Het verkooptarief blijft altijd handmatig aanpasbaar.
        Als factor of salarisindicatie leeg is, laat StaffIQ het verkooptarief
        ongemoeid.
      </p>

      <h3>Proces van aanvraag tot plaatsing</h3>

      <p>
        Het standaardproces loopt als volgt:
      </p>

      <ol>
        <li>
          <b>Aanvraag aanmaken.</b> Leg klantvraag, functie, periode, uren,
          locatie en tariefafspraken vast.
        </li>
        <li>
          <b>Match Personeel openen.</b> StaffIQ neemt criteria uit de aanvraag
          mee naar matching.
        </li>
        <li>
          <b>Kandidaat kiezen.</b> Vanuit matching wordt een
          <b> conceptplaatsing</b> aangemaakt.
        </li>
        <li>
          <b>Plaatsing controleren.</b> Controleer relatie, contactpersoon,
          kandidaat, functie, periode, uren, loon- en factuurafspraken.
        </li>
        <li>
          <b>Voorstellen.</b> Deze stap zet de plaatsing op Voorgesteld. De stap
          kan worden gebruikt als je eerst wilt registreren dat de kandidaat is
          voorgesteld.
        </li>
        <li>
          <b>Voorbeeld klant / Voorb kandidaat.</b> Bekijk de
          plaatsingsbevestigingen voordat je ze opslaat of ter akkoord aanbiedt.
        </li>
        <li>
          <b>Bevestigen klant / Bev kandidaat.</b> Sla de
          plaatsingsbevestiging op bij Documenten.
        </li>
        <li>
          <b>Akkoord klant / Akk kandidaat.</b> Vraag digitaal akkoord via
          WhatsApp, e-mail of beide. Je kunt ook kiezen voor
          <b>wel akkoord, geen WhatsApp of e-mail</b> als het akkoord buiten
          StaffIQ om is gegeven.
        </li>
        <li>
          <b>Starten.</b> Gebruik dit wanneer de plaatsing daadwerkelijk begint.
        </li>
        <li>
          <b>Annuleren.</b> Gebruik dit wanneer de plaatsing niet doorgaat.
        </li>
      </ol>

      <h3>Plaatsingen</h3>

      <p>
        Een plaatsing is de concrete combinatie van relatie, aanvraag en
        kandidaat. De plaatsing begint meestal als <b>Concept</b>. Daarna kun je
        de plaatsing voorstellen, bevestigingen maken, digitaal akkoord vragen en
        de plaatsing starten.
      </p>

      <p>
        De procesknoppen blijven zichtbaar. Knoppen die op dat moment niet
        logisch zijn, worden uitgegrijsd. Daardoor zie je welke stappen al zijn
        gedaan en welke vervolgstappen nog mogelijk zijn.
      </p>

      <p>
        De knoppen <b>Akkoord klant</b> en <b>Akk kandidaat</b> maken een
        digitaal akkoordverzoek. StaffIQ kijkt automatisch naar de
        communicatievinkjes bij de relatie of kandidaat. Als WhatsApp en e-mail
        beide zijn toegestaan, kun je kiezen voor WhatsApp, e-mail of beide.
      </p>

      <p>
        De ontvanger opent de akkoordpagina, bekijkt de PDF en vult naam,
        e-mailadres en eventueel een opmerking in. Bij klantakkoord moet de
        ontvanger bevestigen dat hij of zij bevoegd is namens de organisatie.
        Bij kandidaatakkoord bevestigt de kandidaat akkoord met het document.
      </p>

      <h3>Beschikbaarheid</h3>

      <p>
        In Beschikbaarheid leg je per personeelslid vast wanneer iemand niet of
        anders beschikbaar is. Denk aan:
      </p>

      <ul>
        <li>Leegloop</li>
        <li>Ziek</li>
        <li>Verlof / vakantie</li>
        <li>Opleiding / cursus</li>
        <li>Niet beschikbaar</li>
        <li>Beschikbaar</li>
      </ul>

      <p>
        Per regel leg je medewerker, type, begindatum, einddatum, uren per dag,
        opmerking en actief/inactief vast. Deze gegevens worden gebruikt in het
        planbord.
      </p>

      <h3>Planning</h3>

      <p>
        Planning toont het planbord. Het planbord heeft twee weergaven:
        <b>Relaties</b> en <b>Personeel</b>. Het bord begint altijd op maandag
        en toont meerdere weken naast elkaar. Je kunt vooruit en terug per week
        en het aantal weken wijzigen.
      </p>

      <p>
        In de weergave <b>Relaties</b> staan links de relaties en rechts de
        planning per dag. Bij één geplande medewerker toont de cel de naam van
        de medewerker. Bij meerdere medewerkers toont de cel het aantal.
      </p>

      <p>
        In de weergave <b>Personeel</b> staan links de medewerkers en rechts de
        planning per dag. Bij één plaatsing toont de cel de relatie. Bij meerdere
        plaatsingen toont de cel het aantal.
      </p>

      <p>
        De kleuren geven de status aan:
      </p>

      <ul>
        <li><b>Groen</b>: plaatsing / ingepland</li>
        <li><b>Oranje</b>: leegloop</li>
        <li><b>Rood</b>: ziek</li>
        <li><b>Geel</b>: verlof / vakantie</li>
        <li><b>Paars</b>: opleiding / cursus</li>
        <li><b>Wit</b>: nog inplannen / vrij</li>
      </ul>

      <p>
        Klik op een cel om de achterliggende informatie te bekijken. Klik links
        op een relatie of personeelslid om de kaart te openen.
      </p>

      <h2 style={sectionStyle}>Instellingen en rechten</h2>

      <p>
        Alleen de administrator kan instellingen aanpassen. Denk hierbij aan
        documentmappen, factuur- en offerteteksten, factuursjablonen,
        communicatie-instellingen, digitale akkoordteksten, gebruikers, rechten
        en bedrijfscodes.
      </p>

      <p>
        Bij <b>Instellingen &gt; Digitale accordering</b> beheer je de teksten
        voor WhatsApp, e-mail en akkoordbevestigingen. Er zijn aparte teksten
        voor offertes/opdrachten en voor plaatsingsbevestigingen klant en
        kandidaat.
      </p>

      <h2 style={sectionStyle}>In ontwikkeling</h2>

      <p>
        StaffIQ wordt verder uitgebreid. Onderdelen die in ontwikkeling zijn of
        later worden toegevoegd zijn onder andere:
      </p>

      <ul>
        <li>Direct plannen vanuit het planbord</li>
        <li>Bezettingsoverzicht</li>
        <li>Urenregistratie</li>
        <li>Managementinformatie en dashboard</li>
        <li>Materiaal / producten / stuklijst</li>
        <li>Inkoop, magazijn en voorraad</li>
        <li>Pakbonnen, nacalculatie en managementinformatie</li>
      </ul>

      <p style={smallStyle}>
        StaffIQ blijft in ontwikkeling. Suggesties zijn welkom via
        info@staffiq.nl of via www.staffiq.nl.
      </p>
    </div>
  );
}
